import React, { useState } from 'react';

import { useRouter } from 'next/router';
import { useToasts } from 'react-toast-notifications';

import Button from '@components/common/Button/Button';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import IconCheckmark from '@components/common/IconCheckmark/IconCheckmark';
import ConfirmModal from '@components/common/Modals/ConfirmModal/ConfirmModal';
import PotService from '@services/domain/Lpc/PotService';
import { ROUTE } from '@services/http/Route';

type DeletePotButtonProps = {
    customClass: string;
    amountLeft: number;
    potRef: string;
    children?: React.ReactNode;
    isDashboardButton?: boolean;
};

const DeletePotButton = ({ customClass, amountLeft, potRef, children, isDashboardButton }: DeletePotButtonProps) => {
    const { t } = useTranslateLpc(['common', 'lpc-pot']);
    const { addToast } = useToasts();
    const router = useRouter();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isConfirmDeletePotModalVisible, setIsConfirmDeletePotModalVisible] = useState<boolean>(false);
    const [isRefundAllParticipantModalVisible, setIsRefundAllParticipantModalVisible] = useState<boolean>(false);
    const [isContactServiceModalVisible, setIsContactServiceModalVisible] = useState<boolean>(false);
    const [isCantDelete, setIsCantDelete] = useState<boolean>(false);

    const onDeletePot = (refund: boolean) => {
        setIsLoading(true);
        PotService.deletePot(potRef, refund)
            .then(() => {
                setIsConfirmDeletePotModalVisible(false);
                setIsRefundAllParticipantModalVisible(false);
                addToast(t(`success.UPDATE_SUCCESS`), {
                    appearance: 'success',
                    autoDismiss: true,
                });
                isDashboardButton ? window.location.reload() : router.push(ROUTE.LPC.HOME);
            })
            .catch((err) => {
                if (err.code === 'ERROR_DELETING_POT_AMOUNT_LEFT') {
                    setIsCantDelete(true);
                } else {
                    setIsContactServiceModalVisible(true);
                }
            })
            .finally(() => {
                setIsLoading(false);
                setIsRefundAllParticipantModalVisible(false);
                setIsConfirmDeletePotModalVisible(false);
            });
    };

    const handleClick = () => {
        return amountLeft > 0 ? setIsRefundAllParticipantModalVisible(true) : setIsConfirmDeletePotModalVisible(true);
    };

    return (
        <>
            {/* Delete Pot */}

            <ConfirmModal
                onHide={() => setIsConfirmDeletePotModalVisible(false)}
                onConfirm={() => onDeletePot(false)}
                title={t('delete-pot-modal.title')}
                text={t('delete-pot-modal.text')}
                confirmLabel={t('delete-pot-modal.confirm')}
                cancelLabel={t('delete-pot-modal.cancel')}
                isVisible={isConfirmDeletePotModalVisible}
                iconComponent={<IconCheckmark variant={'warning'} />}
                isLoading={isLoading}
            />

            {/* Refund All Participants */}

            <ConfirmModal
                onHide={() => setIsRefundAllParticipantModalVisible(false)}
                onConfirm={() => onDeletePot(true)}
                title={t('refund-all-participant-modal.title')}
                text={t('refund-all-participant-modal.text')}
                confirmLabel={t('refund-all-participant-modal.confirm')}
                cancelLabel={t('refund-all-participant-modal.cancel')}
                isVisible={isRefundAllParticipantModalVisible}
                iconComponent={<IconCheckmark variant={'warning'} />}
                isLoading={isLoading}
            />

            {/* Contact Service */}

            <ConfirmModal
                onHide={() => setIsContactServiceModalVisible(false)}
                onConfirm={() => router.push(ROUTE.LPC.HELP_CENTER.CONTACT)}
                title={t('contact-service-modal.title')}
                text={t('contact-service-modal.text')}
                confirmLabel={t('contact-service-modal.confirm')}
                cancelLabel={t('refund-all-participant-modal.cancel')}
                isVisible={isContactServiceModalVisible}
                iconComponent={<IconCheckmark variant={'warning'} />}
                isLoading={isLoading}
            />

            {/* Can't delete */}

            <ConfirmModal
                onHide={() => setIsCantDelete(false)}
                title={t('cant-delete-modal.title')}
                text={t('cant-delete-modal.text')}
                cancelLabel={t('cant-delete-modal.cancel')}
                isVisible={isCantDelete}
                iconComponent={<IconCheckmark variant={'warning'} />}
                isLoading={isLoading}
            />

            <Button
                onClick={handleClick}
                type={'button'}
                variant={!isDashboardButton ? 'error' : ''}
                customClass={customClass}
            >
                {children || t('settings.general.delete-cta')}
            </Button>
        </>
    );
};

export default DeletePotButton;
